import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  EmptyComponent,
  SearchComponent,
  SelectComponent,
  SkeletonComponent,
  TableComponent,
  type ActionEvent,
  type SortChangeEvent,
  type TableConfig,
} from 'nebula-ui-kit';
import { finalize, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AdminTenantsService } from '../../../../data-access/src/lib/admin-users/admin-tenants.service';
import { AdminUsersService } from '../../../../data-access/src/lib/admin-users/admin-users.service';
import { inferAdminUserTenantsFromCatalog, mergeAdminUserTenants } from '../../../../data-access/src/lib/admin-users/admin-user-normalize.util';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import { ModulePermissionsService } from '../../../../data-access/src/lib/module-permissions/module-permissions.service';
import { tenantHeaders } from '../../../../data-access/src/lib/shared/tenant-context.util';
import type { AdminSystemUser, AdminTenantSummary } from '../../../../domain/src/lib/admin-users/admin-user.model';
import type { IngestProject } from '../../../../domain/src/lib/ingest/ingest-project.model';
import type {
  FolderPermission,
  ModulePermission,
  ModulePermissionListParams,
  VaultModuleName,
} from '../../../../domain/src/lib/module-permissions/module-permission.model';
import { VAULT_MODULE_OPTIONS } from '../../../../domain/src/lib/module-permissions/module-permission.model';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultPageAlertComponent,
} from '@nebula-shared/ui';
import {
  bootstrapVaultAdminModuleAccess,
  createAdminModuleAccessSignal,
} from '../admin-users/vault-admin-module-access.util';
import {
  VAULT_SINGLE_TENANT_MODE,
  VaultSessionService,
  persistVaultTenantCatalog,
  readVaultItemsPerPageFromPreferences,
  VAULT_TABLE_PAGE_SIZE_OPTIONS,
  VAULT_TABLE_PAGINATION_LABELS,
} from '@nebula/shared-vault/data-access';
import {
  bindVaultTablePageSizeToPreferences,
  createVaultTablePageSizeSignal,
} from '../vault-table-pagination-bind.util';

interface PermissionRow {
  id: string;
  userLabel: string;
  tenantLabel: string;
  moduleLabel: string;
  statusLabel: string;
  is_enabled: boolean;
  module_name: string;
}

@Component({
  selector: 'app-permissions',
  templateUrl: './permissions.component.html',
  styleUrls: ['./permissions.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    ButtonComponent,
    SelectComponent,
    CheckboxComponent,
    SearchComponent,
    TableComponent,
    EmptyComponent,
    SkeletonComponent,
    BadgeComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent,
    VaultPageAlertComponent,
  ],
})
export class PermissionsComponent implements OnInit {
  private readonly permissionsApi = inject(ModulePermissionsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly usersApi = inject(AdminUsersService);
  private readonly tenantsApi = inject(AdminTenantsService);
  private readonly dashboard = inject(DashboardService);
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly vaultSession = inject(VaultSessionService);

  readonly singleTenantMode = VAULT_SINGLE_TENANT_MODE;

  private tenantCatalogInferenceOptions() {
    const scopeTenantId = this.vaultSession.getActiveTenantContext().tenantId;
    const actor = this.vaultSession.getAdminUsersActor();
    return {
      scopeTenantId,
      allowScopeInference: actor === 'admin_empresa' && !!scopeTenantId.trim(),
    };
  }

  readonly moduleOptions = VAULT_MODULE_OPTIONS;
  readonly canManage = createAdminModuleAccessSignal('superadmin_only');

  readonly loading = signal(true);
  readonly saving = signal(false);
  /** `true` cuando `GET /api/module-permissions` responde 5xx (fallo de backend/BD). */
  readonly modulePermissionsApiDown = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly permissions = signal<ModulePermission[]>([]);
  readonly users = signal<AdminSystemUser[]>([]);
  readonly tenants = signal<AdminTenantSummary[]>([]);

  readonly searchTerm = signal('');
  /** Usuario/empresa al crear permiso (desde Gestión de usuarios); no filtra la tabla. */
  readonly prefillUserId = signal('');
  readonly prefillTenantId = signal('');
  readonly filterModule = signal('');
  readonly filterTenant = signal('');
  readonly tablePageSize = createVaultTablePageSizeSignal();
  readonly tablePageSizeOptions = [...VAULT_TABLE_PAGE_SIZE_OPTIONS];
  readonly tableSortColumn = signal<string | null>('userLabel');
  readonly tableSortDirection = signal<'asc' | 'desc' | null>('asc');

  readonly createOpen = signal(false);
  readonly editOpen = signal(false);
  readonly deleteOpen = signal(false);
  readonly foldersOpen = signal(false);

  readonly formUserId = signal('');
  readonly formTenantId = signal('');
  readonly formModule = signal<VaultModuleName>('ingest');
  readonly formEnabled = signal(true);
  readonly selectedProjectIds = signal<number[]>([]);
  readonly ingestProjects = signal<IngestProject[]>([]);
  readonly loadingProjects = signal(false);

  readonly editingPermission = signal<ModulePermission | null>(null);
  readonly permissionToDelete = signal<ModulePermission | null>(null);
  readonly folderPermission = signal<ModulePermission | null>(null);
  readonly folderProjectId = signal('');
  readonly removeFolderOpen = signal(false);
  readonly folderToRemove = signal<FolderPermission | null>(null);

  readonly userOptions = computed(() =>
    this.users()
      .filter((u) => !u.is_superadmin)
      .map((u) => ({ label: `${u.full_name} (${u.email})`, value: u.id })),
  );

  readonly tenantOptions = computed(() =>
    this.tenants().map((t) => ({ label: t.name, value: t.id })),
  );

  readonly moduleSelectOptions = computed(() =>
    this.moduleOptions.map((m) => ({ label: m.label, value: m.value })),
  );

  readonly filterModuleOptions = computed(() => [
    { label: 'Todos los módulos', value: '' },
    ...this.moduleSelectOptions(),
  ]);

  readonly filterTenantOptions = computed(() => [
    { label: 'Todas las empresas', value: '' },
    ...this.tenantOptions(),
  ]);

  readonly filteredPermissions = computed(() => {
    const q = this.searchTerm().trim().toLowerCase();
    return this.permissions().filter((p) => {
      if (!q) return true;
      return (
        (p.user_email ?? '').toLowerCase().includes(q) ||
        (p.user_full_name ?? '').toLowerCase().includes(q) ||
        (p.tenant_name ?? '').toLowerCase().includes(q)
      );
    });
  });

  readonly tableData = computed<PermissionRow[]>(() =>
    this.filteredPermissions().map((p) => ({
      id: p.id,
      userLabel: p.user_full_name || p.user_email || p.user_id,
      tenantLabel: p.tenant_name || p.tenant_id,
      moduleLabel:
        this.moduleOptions.find((m) => m.value === p.module_name)?.label ?? p.module_name,
      statusLabel: p.is_enabled ? 'Habilitado' : 'Deshabilitado',
      is_enabled: p.is_enabled,
      module_name: p.module_name,
    })),
  );

  readonly tableConfig = computed<TableConfig<PermissionRow>>(() => ({
    columns: [
      { key: 'userLabel', label: 'Usuario', align: 'left', sortable: true },
      { key: 'tenantLabel', label: 'Empresa', align: 'left', sortable: true },
      { key: 'moduleLabel', label: 'Módulo', align: 'left', sortable: true },
      { key: 'statusLabel', label: 'Estado', align: 'center', width: '120px', cellTemplate: true },
    ],
    actions: [
      { label: 'Editar', icon: 'edit', action: 'edit' },
      {
        label: 'Carpetas',
        icon: 'Folder',
        action: 'folders',
        visible: (row) => row.module_name === 'ingest',
      },
      { label: 'Eliminar', icon: 'delete', action: 'delete', color: 'warn' },
    ],
    showPagination: true,
    pageSize: readVaultItemsPerPageFromPreferences(),
    pageSizeOptions: [...VAULT_TABLE_PAGE_SIZE_OPTIONS],
    paginationLabels: { ...VAULT_TABLE_PAGINATION_LABELS },
    initialSort: { column: 'userLabel', direction: 'asc' },
    ariaLabel: 'Permisos de módulos',
    actionsLabel: 'Acciones',
    rowClickable: true,
    defaultAction: 'edit',
  }));

  readonly isIngestModule = computed(() => this.formModule() === 'ingest');

  readonly formTenantLabel = computed(() => {
    const tenantId = this.formTenantId().trim();
    if (!tenantId) return 'Sin empresa asignada';
    const fromCatalog = this.tenants().find((t) => t.id === tenantId);
    if (fromCatalog?.name) return fromCatalog.name;
    const user = this.users().find((u) => u.id === this.formUserId());
    const fromUser = user?.tenants?.find((t) => t.tenant_id === tenantId);
    if (fromUser?.tenant_name?.trim()) return fromUser.tenant_name.trim();
    return tenantId;
  });

  readonly prefillTenantLabel = computed(() => {
    const tenantId = this.prefillTenantId().trim();
    if (!tenantId) return '';
    const fromCatalog = this.tenants().find((t) => t.id === tenantId);
    if (fromCatalog?.name) return fromCatalog.name;
    const user = this.users().find((u) => u.id === this.prefillUserId());
    const fromUser = user?.tenants?.find((t) => t.tenant_id === tenantId);
    if (fromUser?.tenant_name?.trim()) return fromUser.tenant_name.trim();
    return tenantId;
  });

  readonly showPrefillContext = computed(
    () => this.singleTenantMode && !!this.prefillUserId().trim(),
  );

  ngOnInit(): void {
    bindVaultTablePageSizeToPreferences(this.tablePageSize, this.destroyRef);
    this.applyNavigationPrefill();

    bootstrapVaultAdminModuleAccess(
      this.canManage,
      'superadmin_only',
      this.dashboard,
      this.cdr,
      () => this.loadCatalogs(),
    );
    if (!this.canManage()) this.loading.set(false);
  }

  private applyNavigationPrefill(): void {
    const nav = this.router.getCurrentNavigation();
    const state = (nav?.extras?.state ?? history.state ?? {}) as Record<string, unknown>;
    let userId = String(state['prefillUserId'] ?? '').trim();
    let tenantId = String(state['prefillTenantId'] ?? '').trim();

    const qp = this.route.snapshot.queryParamMap;
    const legacyUserId = (qp.get('userId') ?? '').trim();
    const legacyTenantId = (qp.get('tenantId') ?? '').trim();
    if (!userId && legacyUserId) userId = legacyUserId;
    if (!tenantId && legacyTenantId) tenantId = legacyTenantId;

    if (userId) this.prefillUserId.set(userId);
    if (tenantId) this.prefillTenantId.set(tenantId);

    if (legacyUserId || legacyTenantId) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  private loadCatalogs(): void {
    if (!this.canManage()) return;
    forkJoin({
      users: this.usersApi.list().pipe(catchError(() => of([] as AdminSystemUser[]))),
      tenants: this.tenantsApi.list().pipe(catchError(() => of([] as AdminTenantSummary[]))),
    }).subscribe({
      next: ({ users, tenants }) => {
        persistVaultTenantCatalog(tenants.map((t) => ({ id: t.id, name: t.name })));
        const inferenceOpts = this.tenantCatalogInferenceOptions();
        this.tenants.set(tenants);
        this.users.set(
          users.map((user) =>
            mergeAdminUserTenants(
              user,
              inferAdminUserTenantsFromCatalog(user, tenants, inferenceOpts),
            ),
          ),
        );
        this.applyPrefillContext();
        this.reload({ silent: true });
      },
    });
  }

  private applyPrefillContext(): void {
    const userId = this.prefillUserId().trim();
    if (!userId) return;

    const tenantId =
      this.prefillTenantId().trim() || this.resolveTenantIdForUser(userId);
    if (tenantId) {
      this.prefillTenantId.set(tenantId);
      this.filterTenant.set(tenantId);
      this.formTenantId.set(tenantId);
    }
  }

  reload(opts?: { silent?: boolean }): void {
    if (!this.canManage()) return;
    if (!opts?.silent) this.loading.set(true);
    this.error.set(null);

    const mod = this.filterModule().trim();
    const ten = this.filterTenant().trim();

    const apiParams: ModulePermissionListParams = {};
    if (ten) apiParams.tenant_id = ten;
    if (mod) apiParams.module_name = mod;

    this.permissionsApi
      .list(apiParams)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (rows) => {
          this.modulePermissionsApiDown.set(false);
          this.permissions.set(rows);
        },
        error: (err: unknown) => {
          this.modulePermissionsApiDown.set(true);
          this.permissions.set([]);
          this.setError(err, this.modulePermissionsLoadErrorMessage(err));
        },
      });
  }

  private modulePermissionsLoadErrorMessage(err: unknown): string {
    const detail =
      err instanceof HttpErrorResponse
        ? typeof err.error === 'object' && err.error && 'detail' in err.error
          ? String((err.error as { detail: unknown }).detail)
          : err.message
        : '';
    const base =
      'El API `/api/module-permissions` no está disponible en este entorno (error del servidor). Reporte al equipo de backend: tabla `module_permissions`, tenant activo o despliegue Fase 3.';
    return detail ? `${base} Detalle: ${detail}` : base;
  }

  applyFilters(): void {
    this.reload();
  }

  clearFilters(): void {
    this.filterModule.set('');
    this.filterTenant.set('');
    this.searchTerm.set('');
    if (this.canManage()) this.reload();
  }

  onFilterModuleChange(v: unknown): void {
    this.filterModule.set(String(v ?? ''));
  }

  onFilterTenantChange(v: unknown): void {
    this.filterTenant.set(String(v ?? ''));
  }

  onFormUserIdChange(v: unknown): void {
    const userId = String(v ?? '');
    this.formUserId.set(userId);
    if (!userId || !this.singleTenantMode) return;
    const tenantId = this.resolveTenantIdForUser(userId);
    if (!tenantId) return;
    this.formTenantId.set(tenantId);
    if (this.formModule() === 'ingest') {
      this.loadIngestProjects(tenantId);
    }
  }

  private resolveTenantIdForUser(userId: string): string {
    const id = userId.trim();
    if (!id) return '';
    const user = this.users().find((u) => u.id === id);
    if (user) {
      const inferred = inferAdminUserTenantsFromCatalog(
        user,
        this.tenants(),
        this.tenantCatalogInferenceOptions(),
      );
      const fromUser = inferred[0]?.tenant_id?.trim() ?? user.tenants?.[0]?.tenant_id?.trim();
      if (fromUser) return fromUser;
    }
    const prefill = this.prefillTenantId().trim();
    if (prefill && (!user || user.id === id)) return prefill;
    return this.vaultSession.getActiveTenantContext().tenantId.trim();
  }

  onFolderProjectIdChange(v: unknown): void {
    this.folderProjectId.set(String(v ?? ''));
  }

  onTableSearchChange(value: string): void {
    this.searchTerm.set(value ?? '');
  }

  onTablePageChange(ev: { pageIndex: number; pageSize: number }): void {
    if (ev.pageSize != null) this.tablePageSize.set(ev.pageSize);
  }

  onTableSort(ev: SortChangeEvent<PermissionRow>): void {
    this.tableSortColumn.set(ev.column != null ? String(ev.column) : null);
    this.tableSortDirection.set(ev.direction);
  }

  onTableAction(ev: ActionEvent<PermissionRow>): void {
    const perm = this.permissions().find((p) => p.id === ev.data.id);
    if (!perm) return;
    if (ev.action === 'edit') this.openEdit(perm);
    if (ev.action === 'delete') this.openDelete(perm);
    if (ev.action === 'folders') this.openFolders(perm);
  }

  openCreate(): void {
    if (this.modulePermissionsApiDown()) {
      this.error.set(this.modulePermissionsLoadErrorMessage(null));
      return;
    }
    const uid = this.prefillUserId().trim();
    this.formUserId.set(uid);
    this.formModule.set('ingest');
    this.formEnabled.set(true);
    this.selectedProjectIds.set([]);
    this.ingestProjects.set([]);

    const openWithTenant = (tenantId: string) => {
      this.formTenantId.set(tenantId);
      this.createOpen.set(true);
      if (this.singleTenantMode && uid && tenantId && this.formModule() === 'ingest') {
        this.loadIngestProjects(tenantId);
      }
    };

    const cachedTenant =
      this.prefillTenantId().trim() ||
      (uid ? this.resolveTenantIdForUser(uid) : this.vaultSession.getActiveTenantContext().tenantId.trim());

    if (cachedTenant) {
      openWithTenant(cachedTenant);
      return;
    }

    openWithTenant(this.vaultSession.getActiveTenantContext().tenantId.trim());
  }

  closeCreate(): void {
    this.createOpen.set(false);
  }

  onFormTenantChange(v: unknown): void {
    const tenantId = String(v ?? '');
    this.formTenantId.set(tenantId);
    this.selectedProjectIds.set([]);
    if (this.formModule() === 'ingest' && tenantId) {
      this.loadIngestProjects(tenantId);
    } else {
      this.ingestProjects.set([]);
    }
  }

  onFormModuleChange(v: unknown): void {
    const mod = String(v ?? 'ingest') as VaultModuleName;
    this.formModule.set(mod);
    this.selectedProjectIds.set([]);
    if (mod === 'ingest' && this.formTenantId()) {
      this.loadIngestProjects(this.formTenantId());
    } else {
      this.ingestProjects.set([]);
    }
  }

  private loadIngestProjects(tenantId: string): void {
    this.loadingProjects.set(true);
    this.http
      .get<unknown>('/api/ingest/projects', { headers: tenantHeaders(tenantId) })
      .pipe(
        map((raw) => {
          if (Array.isArray(raw)) return raw as IngestProject[];
          if (raw && typeof raw === 'object') {
            const items =
              (raw as Record<string, unknown>)['items'] ??
              (raw as Record<string, unknown>)['data'];
            if (Array.isArray(items)) return items as IngestProject[];
          }
          return [];
        }),
        finalize(() => this.loadingProjects.set(false)),
      )
      .subscribe({
        next: (list) => this.ingestProjects.set(list),
        error: () => this.ingestProjects.set([]),
      });
  }

  toggleProjectSelection(projectId: number, checked: boolean): void {
    const current = this.selectedProjectIds();
    if (checked) {
      if (!current.includes(projectId)) {
        this.selectedProjectIds.set([...current, projectId]);
      }
    } else {
      this.selectedProjectIds.set(current.filter((id) => id !== projectId));
    }
  }

  isProjectSelected(projectId: number): boolean {
    return this.selectedProjectIds().includes(projectId);
  }

  saveCreate(): void {
    const user_id = this.formUserId().trim();
    const tenant_id = this.formTenantId().trim();
    const module_name = this.formModule();
    if (!user_id || !tenant_id || !module_name) {
      this.error.set('Usuario, empresa y módulo son obligatorios.');
      return;
    }
    const folder_permissions =
      module_name === 'ingest' && this.selectedProjectIds().length > 0
        ? this.selectedProjectIds().map((project_id) => ({ project_id, subfolder_path: null }))
        : [];
    this.saving.set(true);
    this.permissionsApi
      .create({
        user_id,
        tenant_id,
        module_name,
        is_enabled: this.formEnabled(),
        folder_permissions,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.success.set('Permiso creado correctamente.');
          this.closeCreate();
          this.reload();
        },
        error: (err: unknown) => this.setError(err, 'No se pudo crear el permiso.'),
      });
  }

  openEdit(perm: ModulePermission): void {
    this.editingPermission.set({ ...perm });
    this.editOpen.set(true);
  }

  closeEdit(): void {
    this.editOpen.set(false);
    this.editingPermission.set(null);
  }

  onEditEnabledChange(checked: boolean): void {
    const p = this.editingPermission();
    if (p) this.editingPermission.set({ ...p, is_enabled: checked });
  }

  saveEdit(): void {
    const p = this.editingPermission();
    if (!p) return;
    this.saving.set(true);
    this.permissionsApi
      .update(p.id, { is_enabled: p.is_enabled })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.success.set('Permiso actualizado correctamente.');
          this.closeEdit();
          this.reload({ silent: true });
        },
        error: (err: unknown) => this.setError(err, 'No se pudo actualizar el permiso.'),
      });
  }

  openDelete(perm: ModulePermission): void {
    this.permissionToDelete.set(perm);
    this.deleteOpen.set(true);
  }

  closeDelete(): void {
    this.deleteOpen.set(false);
    this.permissionToDelete.set(null);
  }

  confirmDelete(): void {
    const p = this.permissionToDelete();
    if (!p) return;
    this.saving.set(true);
    this.permissionsApi
      .delete(p.id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.success.set('Permiso eliminado correctamente.');
          this.closeDelete();
          this.reload();
        },
        error: (err: unknown) => this.setError(err, 'No se pudo eliminar el permiso.'),
      });
  }

  openFolders(perm: ModulePermission): void {
    this.folderPermission.set(perm);
    this.folderProjectId.set('');
    if (perm.tenant_id) this.loadIngestProjects(perm.tenant_id);
    this.foldersOpen.set(true);
  }

  closeFolders(): void {
    this.foldersOpen.set(false);
    this.folderPermission.set(null);
  }

  addFolderPermission(): void {
    const perm = this.folderPermission();
    const projectId = this.folderProjectId().trim();
    if (!perm || !projectId) return;
    this.saving.set(true);
    this.permissionsApi
      .addFolder(perm.id, { project_id: Number(projectId), subfolder_path: null })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.success.set('Expediente agregado al permiso.');
          this.folderProjectId.set('');
          this.refreshFolderPermission(perm.id);
          this.reload({ silent: true });
        },
        error: (err: unknown) => this.setError(err, 'No se pudo agregar el expediente.'),
      });
  }

  private refreshFolderPermission(id: string): void {
    this.permissionsApi.get(id).subscribe({
      next: (p) => this.folderPermission.set(p),
    });
  }

  openRemoveFolder(folder: FolderPermission): void {
    this.folderToRemove.set(folder);
    this.removeFolderOpen.set(true);
  }

  closeRemoveFolder(): void {
    this.removeFolderOpen.set(false);
    this.folderToRemove.set(null);
  }

  confirmRemoveFolder(): void {
    const perm = this.folderPermission();
    const folder = this.folderToRemove();
    if (!perm || !folder) return;
    this.saving.set(true);
    this.permissionsApi
      .removeFolder(perm.id, folder.id)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.success.set('Expediente quitado del permiso.');
          this.closeRemoveFolder();
          this.refreshFolderPermission(perm.id);
          this.reload({ silent: true });
        },
        error: (err: unknown) => this.setError(err, 'No se pudo quitar el expediente.'),
      });
  }

  ingestProjectOptions(): Array<{ label: string; value: string }> {
    return this.ingestProjects().map((p) => ({
      label: p.name,
      value: String(p.id),
    }));
  }

  private setError(err: unknown, fallback: string): void {
    this.error.set(
      toModuleAccessErrorMessage(err, {
        moduleLabel: 'Permisos de Módulos',
        fallback,
      }),
    );
  }
}
